import "react-day-picker/lib/style.css";

import React from "react";
import ReactDOM from "react-dom";
import moment from "moment-timezone";
import ReactDayPicker, {
  DayModifiers,
  RangeModifier,
  LocaleUtils,
  DayPickerProps,
  Modifiers
} from "react-day-picker";
import styled, { css } from "styled-components";

import { useEventListener } from "src/library/hooks";
import {
  isDisabled,
  isWarning,
  media
} from "src/library/utils";

import { Button } from "../Button";
import YearMonthSelect from "./YearMonthSelect";

export interface IDayRangePickerProps {
  handleDateChange: (range: RangeModifier) => void;
  initialDateRange?: RangeModifier | null;
  date: Date;
  disabledDates?: ComponentDates[];
  warningDates?: ComponentDates[];
  activeDates?: ComponentDates[];
  modifiers?: Partial<Modifiers>;
  activeDate?: Date;
  minDate: Date;
  maxDate: Date;
  maxRangeDays?: number;
  disabledDays?: number[];
  includeInput?: true;
  onDayClick?: DayPickerProps["onDayClick"];
}

export interface ComponentDates {
  component?: JSX.Element;
  date: Date;
}

const DayRangePicker: React.ReactFCWithChildren<IDayRangePickerProps> = props => {
  const [ position, setPosition ] = React.useState({
    x: 0,
    y: 0
  });

  const divRef = React.useRef<HTMLDivElement>(null);
  const calRef = React.useRef<HTMLDivElement>(null);
  const [ show, setShow ] = React.useState(false);
  const [ date, setDate ] = React.useState<RangeModifier | undefined | null>(props.initialDateRange);

  const setOverlayPosition = () => {
    if (show) {
      if (divRef.current) {
        const bounds = divRef.current.getBoundingClientRect();
        let right = bounds.right - bounds.width;

        if (right + 300 > window.outerWidth) {
          right = bounds.right - 300;
        }

        setPosition({
          y: bounds.top + bounds.height,
          x: right
        });
      }
    }
  };

  const handler = React.useCallback(() => {
    setOverlayPosition();
  }, [ divRef ]);

  const clickHandler = React.useCallback(() => {
    if (show) {
      // @ts-ignore
      if (calRef.current && calRef.current.contains && !calRef.current.contains(event.target)) {
        setShow(false);
      }
    }
  }, [ show, calRef ]);

  useEventListener("resize", handler);
  useEventListener("scroll", handler, document.getElementById("main-wrapper"));
  useEventListener("scroll", handler, document.getElementsByClassName("rt-table")[ 0 ]);
  useEventListener("mousedown", clickHandler, document);

  React.useEffect(() => {
    setOverlayPosition();
  }, [ show ]);

  React.useEffect(() => {
    if (!props.initialDateRange) {
      setDate(undefined);
    }
  }, [ props.initialDateRange ]);

  return (
    <>
      {props.includeInput ? (
        <div
          ref={divRef}
          onClick={() => setShow(true)}
        >
          <input
            className="picker-input"
            value={date ? `${moment(date.from).format("DD/MM/YY")} - ${moment(date.to).format("DD/MM/YY")}` : "-"}
            readOnly
          />
        </div>
      ) : (
        <DayRangeComponent {...props} />
      )}

      {ReactDOM.createPortal(
        <Overlay
          {...position}
          className={show ? "active" : ""}
          ref={calRef}
        >
          <DayRangeComponent
            {...props}
            handleDateChange={({ from, to }) => setDate({
              from,
              to
            })}
          />

          <OverlayFooter>
            <Button
              $buttonType="secondary"
              onClick={() => {
                setDate(undefined);
                setShow(false);
              }}
            >
              Close
            </Button>

            <Button
              $buttonType="primary"
              onClick={() => {
                date && props.handleDateChange(date);
                setShow(false);
              }}
            >
              Filter
            </Button>
          </OverlayFooter>
        </Overlay>,
        document.body
      )}
    </>
  );
};

const DayRangeComponent: React.ReactFCWithChildren<IDayRangePickerProps> = ({ ...props }) => {
  const { disabledDates } = props;

  const [ range, setRange ] = React.useState<{ from?: Date | null; to?: Date | null }>(props.initialDateRange ? {
    from: moment.utc(props.initialDateRange.from).toDate(),
    to: moment.utc(props.initialDateRange.to).toDate()
  } : {
    from: undefined,
    to: undefined
  });

  const [ month, setMonth ] = React.useState<Date>();
  const [ activeWarning, setActiveWarning ] = React.useState<JSX.Element | null>();
  const disabledDays = props.disabledDays || [];
  const warningDates = props.warningDates || [];
  const fromMonth = props.minDate;
  const toMonth = props.maxDate;

  const handleDayClick = (day: Date, modifiers?: DayModifiers) => {
    if (modifiers && modifiers.disabled) {
      const activeDisabled = disabledDates?.find(disabled => disabled.date.getDate() === day.getDate());

      setActiveWarning(
        <>
          {props.maxRangeDays ? `The date range is limited to ${props.maxRangeDays} days` : activeDisabled?.component || "Cannot select outside the allowed date range"}
        </>
      );
    } else if (modifiers && modifiers.warning) {
      const activeWarning = warningDates.find(warning => warning.date.getDate() === day.getDate());

      if (activeWarning) {
        setActiveWarning(activeWarning.component);
      }
    } else {
      setActiveWarning(null);
    }
    // @ts-ignore
    const newRange = ReactDayPicker.DateUtils.addDayToRange(day, range);

    setRange(newRange);
    props.handleDateChange(newRange);
  };

  const handleYearMonthChange = (month: Date) => {
    const fromMonth = props.minDate;
    const toMonth = props.maxDate;
    let applyMonth = month;

    if (month < fromMonth) {
      applyMonth = fromMonth;
    }

    if (month > toMonth) {
      applyMonth = toMonth;
    }

    setMonth(applyMonth);
  };

  const { from, to } = range as RangeModifier;

  const modifiers = {
    ...props.modifiers,
    disabled: (day: Date) => isDisabled(day, disabledDates) || day > props.maxDate || day < props.minDate || disabledDays.includes(day.getDay()),
    warning: (day: Date) => isWarning(day, warningDates),
    start: from,
    end: to
  };

  return (
    <Wrapper className={props.includeInput ? "overlay" : ""}>
      <StyledPicker
        initialMonth={props.date}
        month={month}
        firstDayOfWeek={1}
        numberOfMonths={1}
        fromMonth={props.minDate}
        toMonth={props.maxDate}
        selectedDays={range ? [
          from,
          {
            from,
            to
          }
        ] : undefined}
        modifiers={modifiers}
        onDayClick={(day, dayModifiers, e) => {
          if (props.onDayClick) {
            props.onDayClick(day, dayModifiers, e);
          }

          handleDayClick(day, dayModifiers);
        }}
        captionElement={({ date, localeUtils }: {date: Date; localeUtils: LocaleUtils}) => (
          <YearMonthSelect
            date={date}
            localeUtils={localeUtils}
            onChange={handleYearMonthChange}
            fromMonth={fromMonth}
            toMonth={toMonth}
          />
        )}
      />

      {activeWarning && (
        <WarningComponent>
          {activeWarning}
        </WarningComponent>
      )}
    </Wrapper>
  );
};

const Overlay = styled.div<{ x: number; y: number }>`
  ${({ x, y }) => css`
    display: none;
    flex-direction: column;
    width: 300px;
    padding-top: ${({ theme }) => theme.spacing.padding.md};
    position: absolute;
    z-index: 10;
    background: white;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
    top: ${y}px;
    left: ${x}px;

    &.active {
      display: flex;
    }
  `}
`;

const OverlayFooter = styled.div`
  display: flex;
  flex-direction: row;
  margin: ${({ theme }) => theme.spacing.margins.md};
  button {
    margin: ${({ theme }) => theme.spacing.padding.sm};
    width: 100%;
  }
`;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    min-width: 300px;

    ${media({ lt: theme.breakpoints.sm })} {
      width: 97%;
    }
  `}
`;

const WarningComponent = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  padding: 20px;
`;

const StyledPicker = styled(ReactDayPicker)`
  * {
    outline: none;
  }
  ${({ theme }) => css`
    .DayPicker-Day {
      height: 25px;
      width: 25px;
      font-size: 15px;

      border-radius: 100%;
      outline: none;
      color: ${theme.colors.darkGrey};

      &.DayPicker-Day--start {
        border-top-left-radius: 50% !important;
        border-bottom-left-radius: 50% !important;
      }
      &.DayPicker-Day--end {
        border-top-right-radius: 50% !important;
        border-bottom-right-radius: 50% !important;
      }

      &.DayPicker-Day--warning {
        color: ${theme.colors.warn};
      }

      &.DayPicker-Day--disabled {
        color: ${theme.colors.lightGrey};
      }


      &.DayPicker-Day--withActiveDate {
        position: relative;

        &::before {
          display: block;
          transform: translateX(-50%);
          border-radius: 4px;
          position: absolute;
          background: ${theme.colors.primaryDark};
          height: 4px;
          content: "";
          width: 4px;
          left: 50%;
          bottom: 10%;
        }
      }

      &:not(.DayPicker-Day--outside) {
        &:not(.DayPicker-Day--disabled):hover {
          background-color: ${theme.colors.primaryDark};
        }

        &.DayPicker-Day--selected {
          background-color: ${theme.colors.primaryDark};
          border-radius: 0;

          &.DayPicker-Day--disabled {
            background-color: ${theme.colors.lightGrey};
          }
        }
      }
    }

    .DayPicker-Caption {
      padding: 0;
      font-size: 12px;

      select:first-child {
        margin-right: 5px;
      }
    }

    .DayPicker-Week {
      padding-bottom: 1px;
    }

    .DayPicker-Month,
    .DayPicker-Months {
      ${media({ lt: theme.breakpoints.sm })} {
        width: 97%;
      }
    }
  `}
`;

export default DayRangePicker;
